import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  AddressAutocomplete,
  Alert,
  Button,
  Input,
  addressMetadata,
  verifyAddressMetadata,
  type AddressFields,
} from '@alwindows/shared'
import {
  getHanaSalesOrders,
  hanaAddressLookup,
  postScreenEmailNoAddress,
  postScreenEmailSyteline,
} from '@alwindows/shared/api/screens'
import { useQuote } from '../quote/QuoteContext'
import { EMPTY_QUOTE } from '../quote/types'
import { ROUTE_SELECT_SCREENS } from '../routes'
import { trackAddressEnquiry, trackAddressNotFound, trackAddressSelection } from '../analytics'
import { useIntroVisibility } from '../components/IntroVisibility'
import { useBreadcrumb } from '../components/Breadcrumb'
// Standalone SVGs (one per step) — extracted from the legacy
// `HomeInstructions` graphic. Imported as URLs (Vite default for `.svg`)
// and rendered via <img> so the SVG markup stays out of the JSX.
import iconHouseFinder from '../icons/step-1-house-finder.svg'
import iconScreenMesh from '../icons/step-2-screen-mesh.svg'
import iconCheckDoc from '../icons/step-3-check-doc.svg'
import iconEnvelopeCard from '../icons/step-4-envelope-card.svg'

type Step = 'intro' | 'address' | 'lot-select' | 'no-orders' | 'address-not-found'

// A property/lot the customer can pick when one address maps to several.
// Built from the sales-order rows (only AddrNos that actually have orders
// survive), labelled from the address-lookup row's LotNo + Name1 (builder).
type LotCandidate = {
  addrNo: string
  soNos: string[]
  /** Cleaned lot number (junk normalised to ''), stored on the quote for display. */
  lotNo: string
  label: string
}

// SAP returns sales-order numbers zero-padded ("0000601350") but the items
// endpoint expects them unpadded ("601350"). Strip leading zeros so the
// downstream /v1/screen/items call actually returns rows. Guards against an
// all-zero string collapsing to "".
function normalizeSoNo(so: string): string {
  return so.replace(/^0+/, '') || so
}

// LotNo arrives as junk ("LOT", "", "UNIT") on some SAP records — return the
// real lot or '' so callers don't have to repeat the check.
const LOT_JUNK = new Set(['', 'LOT', 'UNIT', 'NULL'])
function cleanLot(lotNo: string | undefined): string {
  const lot = (lotNo ?? '').trim()
  return lot !== '' && !LOT_JUNK.has(lot.toUpperCase()) ? lot : ''
}

// Human label for a lot option. Falls back to the builder/account name when
// there's no usable lot number.
function lotLabel(lotNo: string | undefined, name1: string | undefined): string {
  const lot = cleanLot(lotNo)
  const name = (name1 ?? '').trim()
  if (lot && name) return `Lot ${lot} — ${name}`
  if (lot) return `Lot ${lot}`
  return name || 'This property'
}

// Marketing-style 4-step graphic shown on first arrival. Ported from the
// legacy `ScreenHomePage` + `HomeInstructions` SVG so customers see the
// whole journey before being dropped into the address autocomplete. Order
// + labels match the legacy app verbatim.
//
// `width` / `height` are the icon container's BASE size in rem (desktop).
// Both shrink proportionally on narrower viewports via the shared
// `--al-icon-scale` CSS variable (×0.7 ≤720px, ×0.5 ≤480px) — see
// ScreenHome.scss — so per-icon tweaks made here flow through to mobile
// without extra plumbing. Heights are intentionally kept equal across
// steps so the badges + dashed connector remain aligned on the same row;
// if you change one step's height, the badge for that column will sit
// slightly lower than its neighbours.
type IntroStep = {
  label: string
  icon: string
  width: number
  height: number
}

const INTRO_STEPS: ReadonlyArray<IntroStep> = [
  { label: 'Find my windows & doors', icon: iconHouseFinder, width: 5, height: 5 },
  { label: 'Select screens & order', icon: iconScreenMesh, width: 3.5, height: 5 },
  { label: 'A&L check details & confirm', icon: iconCheckDoc, width: 4, height: 5 },
  { label: 'Receive email link & pay online', icon: iconEnvelopeCard, width: 7, height: 5 },
]

// The SAP saveOrder payload has no house-number or lot field — SAP parses
// both out of `Street`. Autocomplete picks already carry the number in
// `street` (composed from the Australia Post address lines), but the manual
// form keeps `houseNo` / `lot` / `unit` separate for the HANA lookup, so
// stitch them back the way legacy did (PricingCalculator.js:375-390):
// "Unit 2, 12 Sewells Road" / "Lot 12 Sewells Road". Without this SAP shows
// the order with House No. and Lot blank.
function composeStreet(f: AddressFields): string {
  const number = f.houseNo ? f.houseNo : f.lot ? `Lot ${f.lot}` : ''
  const withNumber = [number, f.street].filter(Boolean).join(' ')
  return f.unit && number ? `Unit ${f.unit}, ${withNumber}` : withNumber
}

export function ScreenHome() {
  const { setQuote } = useQuote()
  const navigate = useNavigate()
  const { setIntroVisible } = useIntroVisibility()

  // Open on the intro graphic; the "Get started" button advances to the
  // address step. The one exception is arriving via the Layout's "Address"
  // progress link from a later step — the customer has already seen the
  // intro and wants to change their address, so skip straight to the form.
  const { state: locationState } = useLocation()
  const skipIntro = Boolean((locationState as { skipIntro?: boolean } | null)?.skipIntro)
  const [step, setStep] = useState<Step>(skipIntro ? 'address' : 'intro')

  // Tell the Layout whether the marketing intro is on screen. While it is,
  // the progress chrome (Address/Select screens/Summary/Submit)
  // stays hidden so it doesn't compete with the 4-step graphic. The moment
  // the user clicks "Get started" they enter step 1 (Address) and the
  // chrome appears.
  useEffect(() => {
    setIntroVisible(step === 'intro')
    return () => setIntroVisible(false)
  }, [step, setIntroVisible])

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [address, setAddress] = useState<AddressFields | null>(null)
  // Control the address widget's entry mode from here so the lot-number note
  // below the intro copy can flip it into manual entry (new-build lots have no
  // street number, so they can only be searched via the manual form's Lot
  // toggle). AddressAutocomplete still drives this back via onModeChange.
  const [addressMode, setAddressMode] = useState<'autocomplete' | 'manual'>('autocomplete')
  // Which slot the manual form opens on. The "Search by lot number" callout
  // sends the user straight to the Lot toggle; the generic "enter it manually"
  // fallback (and returning to search) resets to a street number.
  const [manualNumberType, setManualNumberType] = useState<'house' | 'lot'>('house')
  const handleAddressModeChange = useCallback((m: 'autocomplete' | 'manual') => {
    setAddressMode(m)
    if (m === 'autocomplete') setManualNumberType('house')
  }, [])
  // The address step shows two persistent path cards (street address vs lot
  // number). The selected card stays highlighted and reveals its input below;
  // switching is just clicking the other card — no back link needed. 'none' =
  // nothing picked yet (cards only).
  const [selectedPath, setSelectedPath] = useState<'none' | 'address' | 'lot'>('none')
  // Reset to a clean, unselected state. Used whenever we (re)enter the address
  // step from elsewhere (intro, help screens, lot chooser).
  const resetAddressStep = useCallback(() => {
    setSelectedPath('none')
    setAddressMode('autocomplete')
    setManualNumberType('house')
    setError(null)
  }, [])
  const goToAddress = useCallback(() => {
    setStep('address')
    resetAddressStep()
  }, [resetAddressStep])
  // After a path card is picked, bring its input into view — the form (esp.
  // the manual lot form) can sit below the fold on smaller screens. Guarded
  // with optional chaining so it's a harmless no-op under jsdom.
  const inputSectionRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (selectedPath !== 'none') {
      inputSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
    }
  }, [selectedPath])
  const [helpForm, setHelpForm] = useState({ name: '', email: '', phone: '', address: '' })
  const [helpSent, setHelpSent] = useState(false)
  // Candidates for the lot-select step, populated when one address resolves to
  // more than one order-bearing AddrNo.
  const [lotCandidates, setLotCandidates] = useState<LotCandidate[]>([])

  // Pre-fill the help form's Address field from whatever the user just tried
  // to look up — they've already typed it once, no point making them retype.
  // Only fires when entering the help step with an address present AND the
  // user hasn't already started editing, so we don't clobber in-progress edits.
  useEffect(() => {
    if ((step === 'no-orders' || step === 'address-not-found') && address && !helpForm.address) {
      // Manual entries keep the street number / lot / unit in their own
      // fields — stitch them back (same string as the SAP Street field) so
      // the sales team gets the one identifier they need. Autocomplete picks
      // already carry number and unit in `street` from the Australia Post line.
      const composed = [
        address.houseNo || address.lot ? composeStreet(address) : address.street,
        address.city,
        [address.state, address.postcode].filter(Boolean).join(' '),
      ]
        .filter(Boolean)
        .join(', ')
      setHelpForm((prev) => ({ ...prev, address: composed }))
    }
    // helpForm.address is intentionally NOT a dep — we only seed once per
    // step transition; user edits should not retrigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, address])

  // Register the Layout breadcrumb whenever we're past the intro graphic.
  // useCallback keeps the onClick referentially stable so useBreadcrumb's
  // effect doesn't re-fire on every render.
  const goToIntro = useCallback(() => setStep('intro'), [])
  useBreadcrumb(
    step === 'intro' ? null : { label: 'Buy your screens online now', onClick: goToIntro },
  )

  // Commit a chosen lot to the quote and advance to Select screens. Shared by
  // the auto path (single lot) and the manual lot-select step. `fieldsArg`
  // lets the auto path pass the just-resolved fields directly — the `address`
  // state set at the top of handleAddressSelected hasn't flushed within that
  // same call, so reading it there would be stale.
  const commitLot = (addrNo: string, soNos: string[], lot: string, fieldsArg?: AddressFields) => {
    const f = fieldsArg ?? address
    if (!f) return
    // Legacy fired this once the address resolved to a real record with
    // orders; here it's the moment a concrete lot is committed.
    trackAddressSelection()
    // A new property is a new quote: start from EMPTY_QUOTE and keep only
    // the customer's contact details. Legacy re-initialised its store on
    // every address search; carrying `...prev` here let a refNo from an
    // earlier submitted quote make Submit show "Quote saved" for the new one,
    // with the old promo code and comments riding along.
    setQuote((prev) => ({
      ...EMPTY_QUOTE,
      customer: {
        firstName: prev.customer?.firstName ?? '',
        lastName: prev.customer?.lastName ?? '',
        email: prev.customer?.email ?? '',
        phone: prev.customer?.phone ?? '',
        street: composeStreet(f),
        city: f.city,
        state: f.state,
        postcode: f.postcode,
        addrNo,
        // Lot number for display (Home address). New-build lot searches have
        // no street number, so this is the only identifier that survives.
        lot,
        salesOrg: '', // populated once items load (each item carries SalesOrg)
      },
      salesOrderNo: soNos.join(';'),
    }))
    navigate(ROUTE_SELECT_SCREENS)
  }

  const handleAddressSelected = async (fields: AddressFields) => {
    setAddress(fields)
    setError(null)
    setLoading(true)
    try {
      // Three paths feed the same HANA lookup:
      //   • Autocomplete picks → resolve canonical metadata so we can extract
      //     lot/houseNo/unit/street from the official record.
      //   • Manual entry, street number, no unit → run the typed address
      //     through Australia Post /verify first to canonicalise (catches
      //     typos like "Rd" vs "Road"). Mirrors the legacy app's onViewOptions
      //     flow. Lot entries and entries with a unit bypass verify by
      //     deliberate legacy rule ("Phil changed request to go directly to
      //     HANA DB when Unit entered (Apr 2)").
      //   • Manual entry, lot OR unit present → skip verify, feed the typed
      //     fields straight in.
      let lot = fields.lot ?? ''
      let houseNo = fields.houseNo ?? ''
      let unit = fields.unit
      let street = fields.street
      let suburb = fields.city
      let region = fields.state
      let postcode = fields.postcode

      const applyMeta = (meta: NonNullable<Awaited<ReturnType<typeof addressMetadata>>>) => {
        // Lot from legal_parcel_id ("1/633055" → "1") or lot_identifier.
        if (meta.legal_parcel_id) {
          const split = meta.legal_parcel_id.split('/')
          if (split.length >= 2) lot = split[0]!
        }
        if (!lot && meta.lot_identifier) lot = meta.lot_identifier
        unit = meta.unit_identifier ?? unit
        houseNo = meta.street_number_1 ?? houseNo
        street = meta.street ?? street
        // Legacy stored the verified record's suburb/state/postcode over the
        // typed ones (PricingCalculator.handleHanaAddressLookup), so "Keppera"
        // reached HANA and SAP as "KEPERRA".
        suburb = meta.locality_name ?? suburb
        region = meta.state_territory ?? region
        postcode = meta.postcode ?? postcode
      }

      if (fields.canonicalAddressId) {
        applyMeta(await addressMetadata(fields.canonicalAddressId))
      } else if (fields.houseNo && !fields.unit) {
        const q =
          `${fields.houseNo} ${fields.street} ${fields.city} ${fields.state} ${fields.postcode}`
            .trim()
            .replace(/\s+/g, ' ')
        const meta = await verifyAddressMetadata(q)
        // Best-effort: if AP doesn't recognise the address, fall through to
        // the HANA lookup with the user's typed values rather than erroring.
        if (meta) applyMeta(meta)
      }

      const rows = await hanaAddressLookup({
        unit,
        street,
        postcode,
        lot,
        houseNo,
        suburb,
        region,
      })

      // Postcode is optional on the manual form. Legacy back-filled a blank
      // one from the HANA address record (handleAdvancedSearch) so SAP never
      // received PostCode "".
      if (!postcode && rows.length > 0) postcode = rows[0]!.PostCode ?? ''

      // The address the quote (and the help form) carry from here on. Manual
      // entries keep the typed street NAME unless Australia Post verified it,
      // in which case the canonical name wins — composeStreet stitches the
      // typed number back in front, as legacy did. Autocomplete picks keep
      // their AP street line, number included.
      const resolved: AddressFields = {
        ...fields,
        street: fields.canonicalAddressId ? fields.street : street,
        city: suburb,
        state: region,
        postcode,
      }
      setAddress(resolved)

      if (rows.length === 0) {
        trackAddressNotFound()
        setStep('address-not-found')
        return
      }

      const addrNo = rows.map((r) => r.AddrNo).join(';')
      const sos = await getHanaSalesOrders(addrNo)

      if (sos.length === 0) {
        trackAddressNotFound()
        setStep('no-orders')
        return
      }

      // Group the orders by AddrNo. Only AddrNos that actually carry orders
      // come back from getHanaSalesOrders, so this both filters order-less
      // lots and collapses the address-lookup's duplicate rows. One group =
      // one property/lot the customer could mean. SONos are joined with ';'
      // within a group (legacy parity — one /v1/screen/items call per lot).
      const byAddr = new Map<string, string[]>()
      for (const s of sos) {
        const list = byAddr.get(s.AddrNo) ?? []
        list.push(normalizeSoNo(s.SONo))
        byAddr.set(s.AddrNo, list)
      }

      const candidates: LotCandidate[] = [...byAddr.entries()].map(([addrNo, soNos]) => {
        const row = rows.find((r) => r.AddrNo === addrNo)
        return { addrNo, soNos, lotNo: cleanLot(row?.LotNo), label: lotLabel(row?.LotNo, row?.Name1) }
      })

      // One property → straight through, no prompt (legacy parity: customers
      // with a single lot never saw a chooser). More than one → let the
      // customer pick their own house so we load exactly one home's worth of
      // windows & doors instead of silently merging every lot on the street.
      // Lot for display: prefer SAP's LotNo, else the lot the user typed in a
      // manual lot search (fields.lot).
      if (candidates.length === 1) {
        const c = candidates[0]!
        commitLot(c.addrNo, c.soNos, c.lotNo || (resolved.lot ?? ''), resolved)
        return
      }

      setLotCandidates(candidates)
      setStep('lot-select')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to look up your address.')
    } finally {
      setLoading(false)
    }
  }

  const sendHelpEmail = async (variant: 'no-address' | 'syteline') => {
    if (!helpForm.name || !helpForm.email || !helpForm.phone || !helpForm.address) {
      // Verbatim from legacy HelpForm.js onSubmit.
      setError('Please fill out all the fields.')
      return
    }
    setError(null)
    try {
      if (variant === 'no-address') {
        await postScreenEmailNoAddress(helpForm)
      } else {
        await postScreenEmailSyteline({ ...helpForm, state: address?.state ?? '' })
      }
      trackAddressEnquiry()
      setHelpSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send help request.')
    }
  }

  if (step === 'no-orders' || step === 'address-not-found') {
    return (
      <div className="al-sc-home">
        {/* Heading + intro copy ported verbatim from the legacy
            ScreenPricingCalc modals (helpModal / sytelineModal). Keeping the
            wording 1:1 avoids a "this feels different" reaction from
            existing customers who've used the old flow. */}
        <h2>
          {step === 'address-not-found'
            ? 'We were unable to find your address in our system'
            : 'Sorry'}
        </h2>
        {step === 'address-not-found' ? (
          <p>
            Please try entering your address in a different format if known (street number or lot
            number), or send us your details below and a member of our team will be in touch to
            assist you.
          </p>
        ) : (
          <>
            <p>
              Your window order was raised in a previous version of our IT system and we are unable
              to present the available screens options for you online.
            </p>
            <p>
              Please provide your details below and a member of our team will be in touch to assist
              you with a quote as soon as possible.
            </p>
          </>
        )}
        {helpSent ? (
          <Alert variant="success" title="Request submitted">
            Thank you for submitting your request. A sales representative will be in touch shortly.
          </Alert>
        ) : (
          <>
            <Input
              label="Name"
              required
              autoComplete="name"
              className="input-text"
              value={helpForm.name}
              onChange={(e) => setHelpForm({ ...helpForm, name: e.target.value })}
            />
            <Input
              label="Email"
              required
              type="email"
              autoComplete="email"
              className="input-text"
              value={helpForm.email}
              onChange={(e) => setHelpForm({ ...helpForm, email: e.target.value })}
            />
            <Input
              label="Phone number"
              required
              type="tel"
              className="input-text"
              autoComplete="tel"
              value={helpForm.phone}
              onChange={(e) => setHelpForm({ ...helpForm, phone: e.target.value })}
            />
            <Input
              label="Address"
              required
              autoComplete="street-address"
              className="input-text"
              value={helpForm.address}
              onChange={(e) => setHelpForm({ ...helpForm, address: e.target.value })}
            />
            {error ? <Alert variant="error">{error}</Alert> : null}
            <div className="al-sc__actions">
              <Button variant="outline" onClick={goToAddress}>
                Try a different address
              </Button>
              <Button
                variant="primary"
                onClick={() => sendHelpEmail(step === 'no-orders' ? 'syteline' : 'no-address')}
              >
                Submit
              </Button>
            </div>
            {/* Legacy footer line — verbatim from PRICE_CALC_CALL_ASSISTANCE
                in al_screens/src/constants/Text.js. */}
            <p className="m-tx4 small">
              Alternatively please call us on 1800 ALWINDOWS for assistance.
            </p>
          </>
        )}
      </div>
    )
  }

  if (step === 'lot-select') {
    return (
      <div className="al-sc-home">
        <h2>Which property is yours?</h2>
        <p className="m-bx2">
          We found more than one property registered at this address. Please choose the one
          you&rsquo;re ordering screens for.
        </p>
        <ul className="al-sc-lots">
          {lotCandidates.map((c) => (
            <li key={c.addrNo}>
              {/* Link-styled button: selecting commits the lot + client-side
                  navigates, so it stays a <button> for correct semantics
                  (not an <a href>), reusing the shared button-as-link style. */}
              <button
                type="button"
                className="al-sc-home__link"
                onClick={() => commitLot(c.addrNo, c.soNos, c.lotNo)}
              >
                {c.label}
              </button>
            </li>
          ))}
        </ul>
        <Button variant="outline" onClick={goToAddress}>
          Search a different address
        </Button>
        {error ? <Alert variant="error">{error}</Alert> : null}
      </div>
    )
  }

  if (step === 'intro') {
    return (
      <div className="al-sc-home al-sc-intro text-center">
        <h2 className="no-m-t m-bx5">Buy your screens online now</h2>
        <ol className="al-sc-intro__steps">
          {INTRO_STEPS.map((s, i) => (
            <li key={s.label} className="al-sc-intro__step">
              <div
                className="al-sc-intro__icon"
                style={
                  {
                    // Per-icon container size. The CSS picks these up and
                    // multiplies by --al-icon-scale (set at the steps level
                    // via media queries) so values flow through to mobile.
                    '--icon-w': `${s.width}rem`,
                    '--icon-h': `${s.height}rem`,
                  } as CSSProperties
                }
              >
                <img src={s.icon} alt="" aria-hidden="true" />
              </div>
              <span className="al-sc-intro__num" aria-hidden="true">
                {i + 1}
              </span>
              <p className="al-sc-intro__label uppercase bold xsmall">{s.label}</p>
            </li>
          ))}
        </ol>
        <div className="al-sc-intro__cta m-bx3">
          {/* Legacy used the red-outline `defaultBtnReverse` here — match
              that with our equivalent `primary-outline` so the CTA stays
              an accent rather than a solid red block. */}
          <Button variant="primary-outline" fill onClick={goToAddress}>
            Get started
          </Button>
        </div>
        <p className="al-sc-intro__note text-wrap-balance align-center text-center">
          Please note, A&amp;L screens are only available for customers with A&amp;L windows and
          doors.
        </p>
      </div>
    )
  }

  return (
    <div className="al-sc-home">
      {/* This step identifies the PROPERTY (hence the ADDRESS progress
          label) — the windows and doors it turns up are step 2's business.
          The old heading promised that payoff here, and its "how would you
          like to find it?" left "it" pointing at nothing in particular. */}
      <h2>Find your home</h2>
      <p className="m-bx4">
        We&rsquo;ll look up the windows and doors A&amp;L made for it. Choose how you&rsquo;d like to
        search.
      </p>

      {/* Two persistent path cards, so a customer with only a lot number isn't
          left guessing. Each card is a single button (large target, no nested
          controls); the selected one stays highlighted and reveals its input
          below, and switching is just clicking the other card. */}
      <div className="al-sc-paths">
        <button
          type="button"
          className={`al-sc-path ${selectedPath === 'address' ? 'al-sc-path--active' : ''}`}
          aria-pressed={selectedPath === 'address'}
          onClick={() => {
            setAddressMode('autocomplete')
            setManualNumberType('house')
            setSelectedPath('address')
          }}
        >
          <span className="al-sc-path__title bold">I know my street address</span>
          <span className="al-sc-path__desc xsmall dark-grey">
            Search for your home by its street address.
          </span>
        </button>
        <button
          type="button"
          className={`al-sc-path ${selectedPath === 'lot' ? 'al-sc-path--active' : ''}`}
          aria-pressed={selectedPath === 'lot'}
          onClick={() => {
            setManualNumberType('lot')
            setAddressMode('manual')
            setSelectedPath('lot')
          }}
        >
          <span className="al-sc-path__title bold">I only have a lot number</span>
          <span className="al-sc-path__desc xsmall dark-grey">
            For new builds without a street number yet.
          </span>
        </button>
      </div>

      {selectedPath !== 'none' ? (
        <div ref={inputSectionRef} className="al-sc-home__input m-tx4">
          <AddressAutocomplete
            label={addressMode === 'manual' ? 'Enter your address' : 'Your street address'}
            hideLabel
            mode={addressMode}
            manualNumberType={manualNumberType}
            onModeChange={handleAddressModeChange}
            onChange={handleAddressSelected}
          />
        </div>
      ) : null}
      {loading ? <p className="al-sc-home__loading">Looking up your record…</p> : null}
      {error ? <Alert variant="error">{error}</Alert> : null}
    </div>
  )
}
